import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface RbacRolePillsProps {
	assignment: UserAssignment | null;
}

/**
 * Renders a user's RBAC v2 roles as pills (the union of their assignments).
 *
 * AC5: multi-role users show the union of their roles; effective per-module level
 * is server-authoritative (EffectivePermissionsResource). This never client-computes
 * a most-restrictive intersection — it lists the assigned roles and flags multi-role
 * users as hybrid. Shared between the account users table and any RBAC-aware row.
 */
export const RbacRolePills = ({ assignment }: RbacRolePillsProps) => {
	const roles = assignment?.assignments ?? [];

	if (roles.length === 0) {
		return <span className="text-xs text-gray-400">No roles</span>;
	}

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{roles.map((role) => (
				<span
					key={role.assignment_id}
					className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
				>
					{role.role_name}
					{role.scope_label ? ` · ${role.scope_label}` : ""}
				</span>
			))}
			{roles.length > 1 && (
				<span
					className="rounded bg-blue-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
					title="Multi-role — effective access is the union (most-permissive)"
					data-testid={`hybrid-badge-${assignment?.id}`}
				>
					hybrid
				</span>
			)}
		</div>
	);
};
